import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, startWith, switchMap, take } from 'rxjs/operators';
import { ClinicalResolution } from 'src/app/models/clinical.model';
import { FileUploadResponse } from 'src/app/models/file-upload.model';
import { GlucometerSupplies } from 'src/app/models/glucometer-supplies.model';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { CaseManagementService } from 'src/app/services/case-management/case-management.service';
import { DeviceMappingService } from 'src/app/services/device-mapping/device-mapping.service';
import { DeviceOrderingService } from 'src/app/services/device-ordering/device-ordering.service';
import { OrderRequestApprovedState, OrderRequestVO } from 'src/app/services/device-ordering/value-objects/order.vo';
import { FilesService } from 'src/app/services/files.service';
import { SnackService } from 'src/app/services/snack.service';
import { UtilsService } from 'src/app/services/utils.service';
import { getFileSizeError, getUploadErrorMessage } from 'src/app/shared/utils/file-upload.utils';
import { deviceValidator } from 'src/app/shared/validators/imei-selector.validator';

@Component({
  selector: 'app-confirm-order-dialog',
  templateUrl: './confirm-order-dialog.component.html',
  styleUrls: ['./confirm-order-dialog.component.scss'],
})
export class ConfirmOrderDialogComponent implements OnInit {
  @ViewChild('stepper') stepper: MatStepper;
  @ViewChild('returnLabelInput') returnLabelInput: ElementRef;
  optionForm: UntypedFormGroup;
  confirmForm: UntypedFormGroup;
  inventoryForm: UntypedFormGroup;
  returnLabelForm: UntypedFormGroup;
  shippingForm: UntypedFormGroup;
  approvalForm: UntypedFormGroup;
  showProgressBar = false;
  isLinear = true;
  isOrderApproved = false;

  devicesUnmapped$: Observable<any[]>;
  glucometerSupplies$: Observable<GlucometerSupplies | null>;
  returnLabelFile: File = null;
  returnLabelFileName = '';
  returnLabelUrl = '';
  returnLabelFileId = '';
  isUploadingReturnLabel = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OrderRequestVO,
    private dialogRef: MatDialogRef<ConfirmOrderDialogComponent>,
    private fb: UntypedFormBuilder,
    private deviceOrderService: DeviceOrderingService,
    private deviceUnmappedService: DeviceMappingService,
    private caseManagementService: CaseManagementService,
    private snackService: SnackService,
    public utilService: UtilsService,
    private router: Router,
    private filesService: FilesService,
    private auth: NewAuthService
  ) {}

  // The supply estimate is a Tenovi-glucometer-only feature.
  get isTenoviGlucometer(): boolean {
    const device = this.data?.request_body?.device;
    return device?.manufacturer === 'tenovi' && device?.type === 'glucometer';
  }

  ngOnInit(): void {
    this.optionForm = this.fb.group({ isFromInventory: [false, Validators.required] });
    this.confirmForm = this.fb.group({
      comments: [this.data.comments ? this.data.comments : ''],
    });
    this.inventoryForm = this.fb.group({
      searchByImei: ['', Validators.compose([Validators.required, deviceValidator])],
      comments: [this.data?.comments ? this.data.comments : 'Take from Inventory'],
      providers: ['', Validators.required],
      trackingId: ['', Validators.required],
    });
    this.shippingForm = this.fb.group({
      shippingVerified: [true], // Shipping info is always available, so mark as verified
    });
    this.approvalForm = this.fb.group({
      approved: [false, Validators.requiredTrue], // Requires approval before proceeding
    });
    this.returnLabelForm = this.fb.group({
      includeReturnLabel: [false, Validators.required],
      carrier: [''],
    });
    if (this.isTenoviGlucometer) {
      this.glucometerSupplies$ = this.deviceOrderService.getGlucometerSupplies(
        this.data.request_body?.device?.manufacturer,
        this.data.request_body?.device?.imei
      );
    }
    this.setOptionFormListener();
    this.getUnmappedDevices();
  }

  setOptionFormListener(): void {
    this.optionForm.get('isFromInventory').valueChanges.subscribe((value) => {
      if (value !== undefined && value !== null) {
        this.stepper.next();
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  async approveFromInventory(): Promise<void> {
    this.showProgressBar = true;
    const orderRequest = this.data;
    let orderStatus = orderRequest.request_body.orderStatus;
    if (orderRequest.request_body.device) {
      orderRequest.request_body.device.imei = this.inventoryForm.get('searchByImei').value.device?.imei;
    }
    orderRequest.approved = OrderRequestApprovedState.TRUE;
    orderRequest.comments = this.inventoryForm.get('comments').value;
    if (this.inventoryForm.get('providers').value !== 'full') {
      orderStatus = {
        ...orderStatus,
        order: {
          carrier: this.inventoryForm.get('providers').value,
          tracking_number: this.inventoryForm.get('trackingId').value,
        },
      };
      orderRequest.request_body.orderStatus = orderStatus;
    }
    const trackingUrl = await this.getTrackingProviders();
    orderRequest.request_body.trackingUrl = trackingUrl;
    this.mappingDeviceToPatient(this.data)
      .pipe(
        catchError((error) => {
          this.showProgressBar = false;
          this.snackService.genericSnackBar(error?.error?.message, ['error-snackbar'], 5000);
          return throwError(error);
        })
      )
      .subscribe((resp) => {
        this.showProgressBar = false;
        this.isOrderApproved = true;
        this.approvalForm.get('approved')?.setValue(true);
        this.snackService.genericSnackBar('Device assigned to patient successfully', ['success-snackbar'], 5000);
        this.stepper.next();
      });
  }

  async getTrackingProviders(): Promise<string> {
    const trackingProviders = await this.utilService.getTrackingProviders();
    const carrier = this.inventoryForm.get('providers').value;
    const trackingId = this.inventoryForm.get('trackingId').value;
    let trackingUrl;
    if (carrier === 'full') {
      trackingUrl = trackingId;
    } else {
      trackingUrl =
        `${trackingProviders[carrier]?.['url']}` !== 'none'
          ? `${trackingProviders[carrier]?.['url']}${trackingId}`
          : 'none';
    }
    return trackingUrl;
  }

  approve(): void {
    this.showProgressBar = true;
    const orderRequest = this.data;
    orderRequest.comments = this.confirmForm.get('comments').value;
    orderRequest.request_body = { ...this.data.request_body, comments: orderRequest.comments };
    this.placeDeviceOrder(this.data);
  }

  getUnmappedDevices(): void {
    this.devicesUnmapped$ = this.inventoryForm.get('searchByImei').valueChanges.pipe(
      startWith(''),
      filter((text) => !!text && text.length > 2),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value: string) =>
        this.deviceUnmappedService.getUnmappedDevicesByImei(value, this.data?.request_body?.device?.manufacturer)
      )
    );
  }

  goToPatientChart(): void {
    this.router.navigateByUrl(`clinical/patient-detail/${this.data.patient.id}`);
    this.close();
  }

  displayDevice(device: any): string {
    return device?.device?.imei ? device.device?.imei : '';
  }

  // Return label file upload methods
  openReturnLabelFileDialog(event: Event): void {
    event.preventDefault();
    this.returnLabelInput.nativeElement.click();
  }

  onReturnLabelFileSelected(event: Event): void {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        this.snackService.genericSnackBar(
          'Invalid file type. Please upload PDF, PNG, or JPEG files only.',
          ['error-snackbar'],
          5000
        );
        return;
      }

      const sizeError = getFileSizeError(file);
      if (sizeError) {
        this.snackService.genericSnackBar(sizeError, ['error-snackbar'], 5000);
        return;
      }

      this.returnLabelFile = file;
      this.returnLabelFileName = file.name;
      this.uploadReturnLabel();
    }
  }

  async uploadReturnLabel(): Promise<void> {
    if (!this.returnLabelFile) {
      return;
    }

    this.isUploadingReturnLabel = true;
    const filePath = `documents/${this.data?.client_responsible_id}/${this.data.patient?.id}/return-labels`;

    this.filesService.uploadFile([this.returnLabelFile], filePath).subscribe(
      async (resp: FileUploadResponse) => {
        this.returnLabelUrl = resp.uploadedFiles[0].url;

        // Get the selected carrier from the form
        const selectedCarrier = this.returnLabelForm.get('carrier').value;

        // Calculate expiration date based on carrier
        const returnLabelExpirationDate = this.calculateReturnLabelExpirationDate(selectedCarrier);

        // Save file metadata to Firestore using addPatientFile
        const fileData = {
          title: `Return Label - ${this.returnLabelFileName}`,
          description: `Prepaid return label for device shipment, Expires on ${returnLabelExpirationDate.toDateString()}`,
          url: this.returnLabelUrl,
          carrier: selectedCarrier || '',
          return_label_expiration_date: returnLabelExpirationDate,
          type: 'return-label',
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: {
            id: this.auth.user.user_id,
            name: `${this.auth.user.firstName} ${this.auth.user.lastName}`,
          },
          user_id: this.data.patient?.id,
        };

        try {
          const docRef = await this.filesService.addPatientFile(fileData);
          this.returnLabelFileId = docRef.id;
          this.isUploadingReturnLabel = false;
          this.snackService.genericSnackBar('Return label uploaded successfully', ['success-snackbar'], 3000);
          // Create clinical assessment
          const assessmentData = {
            associations: {
              uid: this.data.patient?.id,
              client_id: this.data.client_responsible_id,
            },
            assessor: this.auth.user.user_id,
            content: `A return shipping label was generated on ${new Date().toDateString()} via ${selectedCarrier} 
            and expires on ${fileData.return_label_expiration_date.toDateString()}`,
            category: 'return-label',
            categoryLabel: 'Return Label',
            date: new Date(),
            pinned: true,
            follow_up: '',
          };
          this.createClinicalAssessment(assessmentData);
        } catch (error) {
          this.isUploadingReturnLabel = false;
          this.snackService.genericSnackBar('Error saving return label metadata', ['error-snackbar'], 5000);
          console.error('Error saving return label metadata:', error);
        }
      },
      (error) => {
        this.isUploadingReturnLabel = false;
        this.snackService.genericSnackBar(getUploadErrorMessage(error), ['error-snackbar'], 5000);
        console.error('Error uploading return label:', error);
      }
    );
  }

  removeReturnLabel(): void {
    this.returnLabelFile = null;
    this.returnLabelFileName = '';
    this.returnLabelUrl = '';
    this.returnLabelFileId = '';
    // Reset the file input
    if (this.returnLabelInput) {
      this.returnLabelInput.nativeElement.value = '';
    }
  }

  private createClinicalAssessment(assessmentData: ClinicalResolution): void {
    this.caseManagementService
      .createClinicalAssessment(assessmentData)
      .then(() => {
        this.snackService.genericSnackBar('Clinical assessment created successfully', ['success-snackbar'], 3000);
      })
      .catch((error) => {
        this.snackService.genericSnackBar('Error creating clinical assessment', ['error-snackbar'], 5000);
        console.error('Error creating clinical assessment:', error);
      });
  }

  /**
   * Calculate return label expiration date based on carrier
   * - UPS: 120 days from upload date
   * - USPS: 365 days from upload date
   * - FedEx: 730 days from upload date
   */
  private calculateReturnLabelExpirationDate(carrier: string): Date {
    const currentDate = new Date();
    const expirationDate = new Date(currentDate);

    switch (carrier?.toLowerCase()) {
      case 'ups':
        expirationDate.setDate(currentDate.getDate() + 120);
        break;
      case 'usps':
        expirationDate.setDate(currentDate.getDate() + 365);
        break;
      case 'fedex':
        expirationDate.setDate(currentDate.getDate() + 730);
        break;
      default:
        // Default to 120 days if carrier is not recognized
        expirationDate.setDate(currentDate.getDate() + 120);
        break;
    }

    return expirationDate;
  }

  private mappingDeviceToPatient(orderRequest: OrderRequestVO) {
    return this.deviceOrderService.mappingDeviceToPatient(orderRequest);
  }

  private placeDeviceOrder(orderRequest: OrderRequestVO) {
    if (this.data?.type === 'supply_order') {
      this.showProgressBar = false;
      this.isOrderApproved = true;
      this.approvalForm.get('approved')?.setValue(true);
      orderRequest.approved = OrderRequestApprovedState.TRUE;
      this.deviceOrderService.updateOrderRequest(orderRequest).then(() => {
        this.snackService.genericSnackBar('Device Order Placed', ['success-snackbar'], 5000);
        this.close();
      });
    } else {
      this.deviceOrderService
        .orderDevice(orderRequest.request_body, orderRequest.id)
        .pipe(take(1))
        .subscribe(
          () => {
            // The backend now owns the final approved/pending state for this async flow: it marks
            // the request approved once the vendor accepts, and reverts it to pending on failure.
            // We only advance the UI optimistically and read the real state back via the Firestore
            // listener — writing `approved` here would race the backend's revert.
            this.showProgressBar = false;
            this.isOrderApproved = true;
            this.approvalForm.get('approved')?.setValue(true);
            this.snackService.genericSnackBar('Order submitted — processing', ['success-snackbar'], 5000);
            this.stepper.next();
          },
          (error) => {
            this.showProgressBar = false;
            this.snackService.genericSnackBar(error?.error?.message, ['error-snackbar'], 6000);
          }
        );
    }
  }
}
